import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowLeftCircleIcon,
  ListBulletIcon,
  Bars3Icon,
  XMarkIcon,
  RectangleGroupIcon,
  ServerStackIcon,
  PresentationChartLineIcon,
  ChatBubbleLeftEllipsisIcon,
  PhotoIcon,
  BoltIcon,
  BuildingOfficeIcon,
  Cog8ToothIcon,
  MapIcon,
  ClipboardDocumentCheckIcon,
  WrenchIcon,
  CreditCardIcon,
  CheckBadgeIcon,
  EyeIcon,
  BellAlertIcon,
  BookOpenIcon,
  UserIcon,
  PuzzlePieceIcon,
} from "@heroicons/react/24/solid";
import SidebarLink from "../layout/SidebarLink";
import Banner from "../layout/Banner";
import { useAppDispatch, useAppSelector } from "../../hooks";
import Ping from "../layout/Ping";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import { logout } from "../../slices/auth";
import { translateText } from "../../utils/translation";
import { capitalise } from "../../utils/text";

export default function Template({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.auth);
  const language = !user ? "EN" : user.language;
  const groups = !user ? [] : user.auth.groups.map((x) => x[1]);

  const navigation = [
    {
      name: translateText("feed", language),
      href: "/feed",
      collapse: null,
      icon: ListBulletIcon,
      showAlert: true,
      allowedGroups: [
        "Feed - Full app access",
        "Runner",
        "Admin",
        "audico staff",
      ],
    },
    {
      name: translateText("rooms", language),
      href: null,
      collapse: [
        {
          name: translateText("overview", language),
          href: "/rooms/overview",
          allowedGroups: [
            "Rooms - Full app access",
            "Runner",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("templates", language),
          href: "/rooms/templates",
          allowedGroups: [
            "Rooms - Full app access",
            "Request templates - Full app access",
            "Admin",
            "audico staff",
          ],
        },
      ],
      icon: RectangleGroupIcon,
      showAlert: false,
      allowedGroups: [
        "Rooms - Full app access",
        "Request templates - Full app access",
        "Runner",
        "Admin",
        "audico staff",
      ],
    },
    {
      name: translateText("devices", language),
      href: "/devices",
      collapse: null,
      icon: ServerStackIcon,
      showAlert: false,
      allowedGroups: ["Devices - Full app access", "Admin", "audico staff"],
    },
    {
      name: translateText("insights", language),
      href: null,
      collapse: [
        {
          name: translateText("analytics", language),
          href: "/insights/analytics",
          allowedGroups: [
            "Insights - Full app access",
            "Family and friends",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("log", language),
          href: "/insights/log",
          allowedGroups: [
            "Insights - Full app access",
            "Family and friends",
            "Admin",
            "audico staff",
          ],
        },
      ],
      icon: PresentationChartLineIcon,
      showAlert: false,
      allowedGroups: [
        "Insights - Full app access",
        "Family and friends",
        "Admin",
        "audico staff",
      ],
    },
    {
      name: translateText("communication", language),
      href: null,
      collapse: [
        {
          name: translateText("announcements", language),
          href: "/communication/announcements",
          allowedGroups: [
            "Communication - Full app access",
            "Family and friends",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("contact_books", language),
          href: "/communication/contact-books",
          allowedGroups: [
            "Communication - Full app access",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("calling", language),
          href: "/communication/calling",
          allowedGroups: [
            "Communication - Full app access",
            "Admin",
            "audico staff",
          ],
        },
        // {"name": translateText("reciprocation", language), "href": "/communication/reciprocation"},
      ],
      icon: ChatBubbleLeftEllipsisIcon,
      showAlert: false,
      allowedGroups: [
        "Communication - Full app access",
        "Family and friends",
        "Marketing",
        "Admin",
        "audico staff",
      ],
    },
    {
      name: translateText("content", language),
      href: null,
      collapse: [
        {
          name: translateText("campaigns", language),
          href: "/content/campaigns",
          allowedGroups: [
            "Content - Full app access",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("photo_albums", language),
          href: "/content/photo-albums",
          allowedGroups: [
            "Content - Full app access",
            "Family and friends",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("events", language),
          href: "/content/events",
          allowedGroups: [
            "Content - Full app access",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("services", language),
          href: "/content/services",
          allowedGroups: [
            "Content - Full app access",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("menus", language),
          href: "/content/menus",
          allowedGroups: [
            "Content - Full app access",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("newsletters", language),
          href: "/content/newsletters",
          allowedGroups: [
            "Content - Full app access",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("web_links", language),
          href: "/content/weblinks",
          allowedGroups: [
            "Content - Full app access",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("trivia", language),
          href: "/content/trivia",
          allowedGroups: [
            "Content - Full app access",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
      ],
      icon: PhotoIcon,
      showAlert: false,
      allowedGroups: [
        "Content - Full app access",
        "Family and friends",
        "Marketing",
        "Admin",
        "audico staff",
      ],
    },
    {
      name: translateText("surveys", language),
      href: "/surveys",
      collapse: null,
      icon: ClipboardDocumentCheckIcon,
      showAlert: false,
      allowedGroups: [
        "Content - Full app access",
        "Marketing",
        "Admin",
        "audico staff",
      ],
    },
    {
      name: translateText("orientation", language),
      href: null,
      collapse: [
        {
          name: translateText("points_of_interest", language),
          href: "/orientation/points-of-interest",
          allowedGroups: [
            "Content - Full app access",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("directions", language),
          href: "/orientation/directions",
          allowedGroups: [
            "Content - Full app access",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("vicinity", language),
          href: "/orientation/vicinity",
          allowedGroups: [
            "Content - Full app access",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
      ],
      icon: MapIcon,
      showAlert: false,
      allowedGroups: [
        "Content - Full app access",
        "Marketing",
        "Admin",
        "audico staff",
      ],
    },
    {
      name: translateText("integration", language),
      href: null,
      collapse: [
        {
          name: translateText("configuration", language),
          href: "/integration/configuration",
          allowedGroups: [
            "Integration - Full app access",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("history", language),
          href: "/integration/history",
          allowedGroups: [
            "Integration - Full app access",
            "Admin",
            "audico staff",
          ],
        },
      ],
      icon: WrenchIcon,
      showAlert: false,
      allowedGroups: ["Integration - Full app access", "Admin", "audico staff"],
    },
    {
      name: translateText("smart_control", language),
      href: null,
      collapse: [
        {
          name: translateText("appliances", language),
          href: "/smart-control/appliances",
          allowedGroups: [
            "Smart control - Full app access",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("groups", language),
          href: "/smart-control/groups",
          allowedGroups: [
            "Smart control - Full app access",
            "Admin",
            "audico staff",
          ],
        },
      ],
      icon: BoltIcon,
      showAlert: false,
      allowedGroups: [
        "Smart control - Full app access",
        "Admin",
        "audico staff",
      ],
    },
    {
      name: translateText("payment", language),
      href: null,
      collapse: [
        {
          name: translateText("items", language),
          href: "/payment/items",
          allowedGroups: ["Payment - Full app access", "Admin", "audico staff"],
        },
      ],
      icon: CreditCardIcon,
      showAlert: false,
      allowedGroups: ["Payment - Full app access", "Admin", "audico staff"],
    },
    {
      name: translateText("tasks", language),
      href: null,
      collapse: [
        {
          name: translateText("manage", language),
          href: "/tasks/manage",
          allowedGroups: ["Tasks - Full app access", "Admin", "audico staff"],
        },
        {
          name: translateText("logs", language),
          href: "/tasks/logs",
          allowedGroups: ["Tasks - Full app access", "Admin", "audico staff"],
        },
      ],
      icon: CheckBadgeIcon,
      showAlert: false,
      allowedGroups: ["Tasks - Full app access", "Admin", "audico staff"],
    },
    {
      name: translateText("property", language),
      href: null,
      collapse: [
        {
          name: translateText("catalog", language),
          href: "/property/catalog",
          allowedGroups: [
            "Property - Full app access",
            "Admin",
            "audico staff",
          ],
        },
        {
          name: translateText("availability", language),
          href: "/property/availability",
          allowedGroups: [
            "Property - Full app access",
            "Admin",
            "audico staff",
          ],
        },
      ],
      icon: BuildingOfficeIcon,
      showAlert: false,
      allowedGroups: ["Property - Full app access", "Admin", "audico staff"],
    },
    {
      name: translateText("third_party", language),
      href: null,
      collapse: [
        {
          name: translateText("prompts", language),
          href: "/third-party/prompts",
          allowedGroups: [
            "Third party - Full app access",
            "Marketing",
            "Admin",
            "audico staff",
          ],
        },
      ],
      icon: PuzzlePieceIcon,
      showAlert: false,
      allowedGroups: [
        "Content - Full app access",
        "Marketing",
        "Admin",
        "audico staff",
      ],
    },
    {
      name: translateText("settings", language),
      href: null,
      collapse: [
        {
          name: translateText("profile", language),
          href: "/settings/profile",
          allowedGroups: [
            "Settings - Full app access",
            "Admin",
            "audico staff",
          ],
        },
      ],
      icon: Cog8ToothIcon,
      showAlert: false,
      allowedGroups: ["Settings - Full app access", "Admin", "audico staff"],
    },
    {
      name: translateText("overview", language),
      href: "/oversight/overview",
      collapse: null,
      icon: EyeIcon,
      showAlert: false,
      allowedGroups: ["Executive"],
    },
    {
      name: capitalise(translateText("requests", language)),
      href: "/oversight/feed",
      collapse: null,
      icon: BellAlertIcon,
      showAlert: false,
      allowedGroups: ["Executive"],
    },
    {
      name: translateText("communication", language),
      href: "/oversight/communication",
      collapse: null,
      icon: ChatBubbleLeftEllipsisIcon,
      showAlert: false,
      allowedGroups: ["Executive"],
    },
    {
      name: translateText("devices", language),
      href: "/oversight/devices",
      collapse: null,
      icon: ServerStackIcon,
      showAlert: false,
      allowedGroups: ["Executive"],
    },
    {
      name: translateText("content", language),
      href: "/oversight/content",
      collapse: null,
      icon: PhotoIcon,
      showAlert: false,
      allowedGroups: ["Executive"],
    },
    {
      name: translateText("log", language),
      href: "/oversight/log",
      collapse: null,
      icon: BookOpenIcon,
      showAlert: false,
      allowedGroups: ["Executive"],
    },
    {
      name: translateText("orientation", language),
      href: "/oversight/orientation",
      collapse: null,
      icon: MapIcon,
      showAlert: false,
      allowedGroups: ["Executive"],
    },
    {
      name: translateText("integration", language),
      href: "/oversight/integration",
      collapse: null,
      icon: WrenchIcon,
      showAlert: false,
      allowedGroups: ["Executive"],
    },
    {
      name: translateText("smart_control", language),
      href: "/oversight/smart-control",
      collapse: null,
      icon: BoltIcon,
      showAlert: false,
      allowedGroups: ["Executive"],
    },
    {
      name: translateText("tasks", language),
      href: "/oversight/tasks",
      collapse: null,
      icon: CheckBadgeIcon,
      showAlert: false,
      allowedGroups: ["Executive"],
    },
    {
      name: translateText("users", language),
      href: "/oversight/users",
      collapse: null,
      icon: UserIcon,
      showAlert: false,
      allowedGroups: ["Executive"],
    },
  ];

  const filteredNavigation = navigation
    .filter((item) => checkUserPermitted(item.allowedGroups))
    .map((item) => {
      if (item.collapse === null) return item;
      const filteredCollapse = item.collapse.filter((subItem) =>
        checkUserPermitted(subItem.allowedGroups),
      );
      item.collapse = filteredCollapse;
      return item;
    });

  // Set state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarWidth, setDesktopSidebarWidth] = useState<72 | 20>(20);
  const [windowSize, setWindowSize] = useState([
    window.innerWidth,
    window.innerHeight,
  ]);

  // Load effects
  useEffect(() => {
    const handleWindowResize = () => {
      setWindowSize([window.innerWidth, window.innerHeight]);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  // Functions
  function getContentClass() {
    const width = windowSize[0];
    if (width < 1024) return "pl-0";
    return `pl-${desktopSidebarWidth}`;
  }

  async function triggerLogout(e: any) {
    e.preventDefault();
    const language = !user ? "EN" : user.language;
    const res: any = await dispatch(logout());
    if (res.payload.status === 205) {
      dispatch(
        addAlert(generateAlertText("logout", language), "success", 5000),
      );
    } else {
      dispatch(addAlert(generateAlertText("logout_error", language), "danger"));
    }
  }

  function checkUserPermitted(allowedGroups: string[]) {
    const commonGroups = groups.filter((value) =>
      allowedGroups.includes(value),
    );
    return commonGroups.length > 0;
  }

  // Extract data from Redux store
  const { actionable } = useAppSelector((state) => state.requests);
  const actionableQueue = actionable.queue === null ? [] : actionable.queue;

  return (
    <div>
      {/* Left side pullover for small viewports */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-midnight/50" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-midnight px-6 pb-2 ring-1 ring-light/20 scrollbar-invisible">
                  <div className="flex h-16 pt-4 shrink-0 items-center">
                    <a href="/">
                      <img
                        className="h-12 w-auto"
                        src="/img/logo/icon_gradient.svg"
                        alt="audico"
                      />
                    </a>
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul className="-mx-2 space-y-3">
                          {filteredNavigation.map((item, idx) => (
                            <li key={item.name}>
                              <SidebarLink
                                key={idx}
                                item={item}
                                showPing={
                                  actionableQueue.length > 0 && item.showAlert
                                }
                              />
                            </li>
                          ))}
                        </ul>
                      </li>
                      <li className="-mx-6 mt-auto">
                        <a
                          href="#!"
                          className="group flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-light hover:text-white hover:bg-danger-dark/10"
                          onClick={(e) => triggerLogout(e)}
                        >
                          <ArrowLeftCircleIcon
                            className="h-5 w-5 my-auto text-secondary group-hover:text-danger"
                            aria-hidden="true"
                          />
                          <span className="sr-only">Log out</span>
                          <span aria-hidden="true">Log out</span>
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div
        className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex w-${desktopSidebarWidth} lg:flex-col transition-all ease-in-out delay-100`}
        onMouseEnter={() => setDesktopSidebarWidth(72)}
        onMouseLeave={() => setDesktopSidebarWidth(20)}
      >
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex grow flex-col w-full gap-y-5 overflow-y-auto bg-midnight px-4 scrollbar-invisible">
          <div className="flex h-16 pt-4 shrink-0 items-center">
            <a href="/">
              <img
                className="h-12 w-auto"
                src="/img/logo/icon_gradient.svg"
                alt="audico"
              />
            </a>
          </div>
          {desktopSidebarWidth === 20 ? (
            <nav>
              <ul className="flex flex-col items-center space-y-1">
                {filteredNavigation.map((item) => (
                  <li key={item.name}>
                    <a
                      href="#!"
                      className="text-light hover:text-white hover:bg-gray-800 group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold"
                    >
                      <item.icon
                        className="h-6 w-6 shrink-0"
                        aria-hidden="true"
                      />
                      <span className="sr-only">{item.name}</span>
                      {actionableQueue.length > 0 && item.showAlert && (
                        <Ping colour="danger" size={3} position="left-14" />
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ) : (
            <nav className={`flex flex-1 flex-col delay-show`}>
              <ul className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul className="-mx-2 space-y-3">
                    {filteredNavigation.map((item, idx) => (
                      <li key={item.name}>
                        <SidebarLink
                          key={idx}
                          item={item}
                          showPing={
                            actionableQueue.length > 0 && item.showAlert
                          }
                        />
                      </li>
                    ))}
                  </ul>
                </li>

                <li className="-mx-6 mt-auto">
                  <a
                    href="#!"
                    className="group flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-light hover:text-white hover:bg-danger-dark/10"
                    onClick={(e) => triggerLogout(e)}
                  >
                    <ArrowLeftCircleIcon
                      className="h-6 w-6 my-auto text-light group-hover:text-danger"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Log out</span>
                    <span aria-hidden="true">Log out</span>
                  </a>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>

      {/* Sticky top navbar for small viewports */}
      <div className="sticky top-0 z-40 flex items-center justify-start gap-x-6 bg-midnight px-4 py-4 shadow-sm sm:px-6 lg:hidden h-20">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-white hover:bg-light/10 rounded-lg lg:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon className="h-5 w-5" aria-hidden="true" />
        </button>
        {/* <div className="flex-1 text-sm font-semibold leading-6 text-white">Dashboard</div> */}
        {/* <a href="#!">
          <span className="sr-only">Your profile</span>
          <img
            className="h-8 w-8 rounded-full bg-gray-800"
            src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
            alt=""
          />
        </a> */}
      </div>

      {/* Page content */}
      <main className={getContentClass()}>
        <div className="lg:p-10 p-6 h-screen w-full">
          <div className="h-full w-full overflow-y-auto scrollbar-invisible">
            {children}
          </div>

          {/* Alert banner */}
          <Banner />
        </div>
      </main>
    </div>
  );
}
